/**
 * "You are impersonating" — the only thing in the UI that tells an operator
 * the session they are driving is not their own.
 *
 * How a session gets here: the auth worker's Access-gated admin UI mints a
 * short-lived session for a target user and opens `/impersonate#token=…`
 * (../impersonation-handoff), which plants the token in localStorage. The
 * Better Auth client sends it as a Bearer on every auth request and the
 * worker's bearer plugin resolves it, OUTRANKING any first-party cookie — so
 * without this indicator an impersonated tab is visually indistinguishable
 * from the operator's own.
 *
 * SHAPE — a minimal corner tag, by explicit request after two rejected
 * iterations: a full-width top bar overlapped the nav-bar layer and confused
 * fixed-position layouts, and a bottom-left pill with an inline expanding card
 * read as too heavy. What remains is the smallest persistent marker that still
 * works: the single word "Impersonating" tucked into the very top-left corner
 * of the viewport, flush against both edges, opening the DS Popover with the
 * full sentence and the way out. Fixed, so it takes no space in flow and can
 * never shift a layout; the popover portals at the DS's z-[202] above
 * everything, and dismisses on outside-click/Escape like every other popover.
 *
 * Mounted once, from AuthProvider (../session), next to <AuthDialogs />.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Popover } from '@/components/ui/popover';
import { unregisterWebPushBeforeSignOut } from '@/lib/push/web-push';

import { authClient, clearSessionToken } from '../client';

/**
 * The real navigation, injectable ONLY so the flow below is testable — the
 * same trick `healStaleSessionToken` in ../client uses for its reload. jsdom's
 * `window.location` is [LegacyUnforgeable]: neither `assign` nor the `location`
 * property itself can be redefined, so a test can't observe a real navigation.
 * Production always takes the default.
 */
function navigateHome(): void {
  window.location.assign('/');
}

/**
 * End the impersonation: sign out (which kills the session AND its cookies),
 * drop the planted Bearer, reload.
 *
 * Why signOut, not revokeSession: revokeSession only deletes the session row
 * from the DB. It does NOT clear the browser's session cookies, and that
 * matters in two ways:
 *
 *  1. **Cookie cache** — if the worker enables `session.cookieCache`, the
 *     `session_data` cookie holds a short-lived copy of the session payload.
 *     After a DB-only revoke the cache keeps serving the impersonated session
 *     until its TTL expires, which looks like "impersonation didn't stop."
 *
 *  2. **Cookie overwrite** — better-auth refreshes the `session_token` cookie
 *     when `updateAge` is reached. The bearer plugin rewrites the cookie on
 *     the request, so a refresh during impersonation stamps the impersonated
 *     token into the first-party cookie, overwriting the admin's own. After a
 *     DB-only revoke the cookie still carries the (now dead) impersonated
 *     token, which resolves to nothing → the admin is signed out anyway.
 *
 * signOut solves both: its response carries `Set-Cookie` with `maxAge=0` for
 * every session cookie, so the cache and any overwritten token are gone.
 *
 * The tradeoff is that the operator's own session cookie is also cleared if
 * it survived impersonation, so they have to sign in again. Preserving it
 * would require detecting — from client JS — whether the httpOnly cookie was
 * overwritten, which is not possible. The tradeoff (sign-in again vs.
 * impersonation persisting) clearly favours reliability.
 *
 * The signOut failing is expected and tolerated: the impersonated session is
 * short-lived and may already have expired, and the local cleanup below is
 * what the operator actually needs either way.
 *
 * Like every sign-out, it first takes this browser off push while the session
 * still exists: a push registration left behind would keep delivering the
 * impersonated user's notifications to the operator's browser.
 */
export async function stopImpersonating(
  navigate: () => void = navigateHome,
): Promise<void> {
  try {
    await unregisterWebPushBeforeSignOut();
    await Promise.race([
      authClient.signOut(),
      new Promise<void>((r) => setTimeout(r, 5000)),
    ]);
  } catch {
    /* network down / worker unreachable — local cleanup still runs */
  }
  clearSessionToken();
  navigate();
}

export function ImpersonationBanner() {
  const { t } = useTranslation();
  const { data } = authClient.useSession();
  const [stopping, setStopping] = useState(false);

  // The worker declares `impersonatedBy` via `session.additionalFields`
  // (`input: false`), which lives entirely server-side — the client SDK infers
  // its Session type from the CLIENT plugin list, so the field comes back on
  // `get-session` at runtime but is absent from the inferred type. Hence the
  // narrow cast instead of a plugin-wide type augmentation.
  const impersonatedBy = (data?.session as { impersonatedBy?: string | null } | undefined)
    ?.impersonatedBy;

  if (typeof impersonatedBy !== 'string' || impersonatedBy.length === 0) return null;

  const email = data?.user?.email ?? '';

  return (
    <div
      role="status"
      // z-[200] is deliberate: it clears the sidebar (z-[199]) and ties the
      // nav-bar (z-[200]), which this renders after — AuthProvider mounts it
      // below `children` — so the tag paints over the app chrome. The popover
      // itself portals at the DS's z-[202].
      className="fixed left-0 top-0 z-[200]">
      <Popover
        contentProps={{ align: 'start', sideOffset: 2, className: 'w-64' }}
        content={
          <div className="text-xs font-medium leading-snug">
            <p className="mb-2">{t('auth.impersonation.banner', { email })}</p>
            <button
              type="button"
              disabled={stopping}
              onClick={() => {
                setStopping(true);
                void stopImpersonating();
              }}
              className="w-full rounded-md border border-amber-500 bg-amber-100 px-2 py-1 text-xs font-bold text-amber-950 transition-colors hover:bg-amber-200 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-50 dark:text-amber-900 dark:hover:bg-amber-100">
              {t('auth.impersonation.stop')}
            </button>
          </div>
        }>
        <button
          type="button"
          aria-live="polite"
          // The visible text is the single word "Impersonating"; the label
          // carries who, so a screen reader gets the whole fact at once.
          aria-label={t('auth.impersonation.pillLabel', { email })}
          className="rounded-br-md bg-amber-400/90 px-2 py-0.5 text-[11px] font-semibold leading-4 text-amber-950 transition-colors hover:bg-amber-400 dark:bg-amber-600/90 dark:text-amber-50 dark:hover:bg-amber-600">
          {t('auth.impersonation.tag')}
        </button>
      </Popover>
    </div>
  );
}
